import { Injectable } from '@angular/core'
import {
  HeliosHotkeysType,
  HotkeyDestroyFn,
  HotkeyMetadata,
  HotkeysService,
} from '@helios/hotkeys'
import { AccountListKey } from '@helios/panels/account-list'
import { CorporateActionsKey } from '@helios/panels/corporate-actions'
import { DistributionsKey } from '@helios/panels/distributions'
import { ExternalFileManagerKey } from '@helios/panels/external-file-manager'
import { JobsKey } from '@helios/panels/jobs'
import { MultiBreaksKey } from '@helios/panels/multi-breaks'
import { PositionsKey } from '@helios/panels/positions'
import { PricingKey } from '@helios/panels/pricing'
import { TransactionsKey } from '@helios/panels/transactions'
import { RootState, selectFocusedUIKey } from '@helios/state-management'
import { Store } from '@ngrx/store'
import { distinctUntilChanged } from 'rxjs/operators'
import { getAvailableHotkeys } from './helios-hotkeys'

/**
 * This service is what binds the hotkey definitions to the actions they perform.
 * This handles the "global" actions - panel-open commands and app-level things.
 * Panel-specific hotkey actions should be contained within the panel themselves.
 * Panel-open handlers are separate from the panels themselves because of code splitting - the panel code should not be loaded until the panel is added
 */
@Injectable({
  providedIn: 'root',
})
export class HeliosHotkeysService {
  /** @internal */
  readonly bindings: Map<HeliosHotkeysType, HotkeyDestroyFn> = new Map()
  /** @internal */
  readonly availableHotkeys: Map<HeliosHotkeysType, HotkeyMetadata>

  constructor(
    private readonly hotkeysService: HotkeysService<HeliosHotkeysType>,
    private readonly store: Store<RootState>,
  ) {
    this.availableHotkeys = getAvailableHotkeys()
    hotkeysService.register(this.availableHotkeys)
    this.setupStore()
  }

  setupStore() {
    this.store
      .select(selectFocusedUIKey)
      .pipe(distinctUntilChanged())
      .subscribe(focusedUIKey => {
        const scope = focusedUIKey ? focusedUIKey : 'all'
        this.hotkeysService.setScope(scope)
      })
  }

  bind(hotkey: HeliosHotkeysType, callback: () => void) {
    if (!this.availableHotkeys.has(hotkey)) return
    this.bindings.set(
      hotkey,
      this.hotkeysService.bind(hotkey, e => {
        e.preventDefault()
        callback()
        return false
      }),
    )
  }

  bindPanelOpenHotkeys(callback: (panelKey: string) => void) {
    this.bind('focus-breaks', () => callback(MultiBreaksKey))
    this.bind('focus-accounts', () => callback(AccountListKey))
    this.bind('focus-transactions-panel', () => callback(TransactionsKey))
    this.bind('focus-positions', () => callback(PositionsKey))
    this.bind('focus-distributions', () => callback(DistributionsKey))
    this.bind('focus-external-file-manager', () =>
      callback(ExternalFileManagerKey),
    )
    this.bind('focus-pricing', () => callback(PricingKey))
    this.bind('focus-corporate-actions', () => callback(CorporateActionsKey))
    this.bind('focus-jobs', () => callback(JobsKey))
  }

  isBound(hotkey: HeliosHotkeysType) {
    return this.bindings.has(hotkey)
  }

  unbind(hotkey: HeliosHotkeysType) {
    const destroyFn = this.bindings.get(hotkey)
    if (destroyFn) destroyFn()
    this.bindings.delete(hotkey)
  }

  cleanup() {
    const hotkeys: HeliosHotkeysType[] = []
    //make a copy so we can delete in the iterator
    for (const key of this.bindings.keys()) hotkeys.push(key)
    hotkeys.forEach(hotkey => this.unbind(hotkey))
  }
}
